import toast from 'react-hot-toast';

// Track recent errors to prevent duplicates
interface ErrorEntry {
  message: string;
  timestamp: number;
  count: number;
}

class ErrorHandler {
  private recentErrors: Map<string, ErrorEntry> = new Map();
  private readonly ERROR_DEDUPLICATION_WINDOW = 5000; // 5 seconds
  private readonly MAX_ERRORS_BEFORE_COMBINE = 3;
  private isShowingCombinedError = false;

  /**
   * Check if this is a network/backend error
   */
  private isNetworkError(error: any): boolean {
    // Network errors (no response)
    if (!error.response) {
      return true;
    }
    
    // Backend down errors (5xx status codes)
    if (error.response.status >= 500) {
      return true;
    }
    
    // Connection refused, timeout, etc.
    if (error.code === 'ECONNREFUSED' || 
        error.code === 'ETIMEDOUT' || 
        error.code === 'NETWORK_ERROR' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        error.message?.includes('connection')) {
      return true;
    }
    
    return false;
  }

  /**
   * Get a standardized error message for network errors
   */
  private getNetworkErrorMessage(): string {
    return "We've hit a technical glitch. Try again later.";
  }

  /**
   * Clean up old error entries
   */
  private cleanupOldErrors(): void {
    const now = Date.now();
    for (const [key, entry] of this.recentErrors.entries()) {
      if (now - entry.timestamp > this.ERROR_DEDUPLICATION_WINDOW) {
        this.recentErrors.delete(key);
      }
    }
  }

  /**
   * Generate a key for the error to track duplicates
   */
  private getErrorKey(error: any): string {
    if (this.isNetworkError(error)) {
      return 'network_error';
    }
    
    // For other errors, use status code + message as key
    const status = error.response?.status || 'no_response';
    const message = error.response?.data?.detail || 
                   error.response?.data?.error || 
                   error.message || 
                   'unknown_error';
    return `${status}_${message}`;
  }

  /**
   * Show a combined error message for multiple network errors
   */
  private showCombinedError(): void {
    if (this.isShowingCombinedError) {
      return;
    }
    
    this.isShowingCombinedError = true;
    
    toast.error(this.getNetworkErrorMessage(), {
      duration: 8000, // Show longer for combined errors
      style: {
        background: 'var(--toast-bg, #ffffff)',
        color: 'var(--toast-text, #000000)',
        border: '1px solid var(--toast-border, #e5e7eb)',
      },
      className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
    });
    
    // Reset after showing combined error
    setTimeout(() => {
      this.isShowingCombinedError = false;
      this.recentErrors.clear();
    }, this.ERROR_DEDUPLICATION_WINDOW);
  }

  /**
   * Main error handling method
   */
  showError(error: any): void {
    this.cleanupOldErrors();
    
    const errorKey = this.getErrorKey(error);
    const now = Date.now();
    
    if (this.recentErrors.has(errorKey)) {
      const entry = this.recentErrors.get(errorKey)!;
      entry.count++;
      entry.timestamp = now;
      
      // If we have multiple network errors in a short time, show combined message
      if (this.isNetworkError(error) && entry.count >= this.MAX_ERRORS_BEFORE_COMBINE) {
        this.showCombinedError();
        return;
      }
      
      // For non-network errors or first few network errors, show individual message
      if (entry.count <= this.MAX_ERRORS_BEFORE_COMBINE) {
        this.showIndividualError(error);
      }
    } else {
      // First occurrence of this error
      this.recentErrors.set(errorKey, {
        message: this.getErrorMessage(error),
        timestamp: now,
        count: 1
      });
      
      this.showIndividualError(error);
    }
  }

  /**
   * Show individual error message
   */
  private showIndividualError(error: any): void {
    const message = this.getErrorMessage(error);
    
    toast.error(message, {
      style: {
        background: 'var(--toast-bg, #ffffff)',
        color: 'var(--toast-text, #000000)',
        border: '1px solid var(--toast-border, #e5e7eb)',
      },
      className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
    });
  }

  /**
   * Extract error message from error object
   */
  private getErrorMessage(error: any): string {
    // Network errors
    if (this.isNetworkError(error)) {
      return this.getNetworkErrorMessage();
    }
    
    // Backend validation errors
    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }
    
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    
    // Generic error message
    if (error.message) {
      return error.message;
    }
    
    return "An unexpected error occurred. Please try again.";
  }

  /**
   * Clear all tracked errors (useful for testing or manual cleanup)
   */
  clearErrors(): void {
    this.recentErrors.clear();
    this.isShowingCombinedError = false;
  }
}

// Export singleton instance
export const errorHandler = new ErrorHandler();

// Export the class for testing purposes
export { ErrorHandler };
